// VideoLink — /mockup
// Galerie interne (non listée) des modes de sous-titres proposés pour le
// LLYLL Subtitler. Maquettes CSS animées : approximation fidèle du rendu
// ffmpeg réel (mêmes couleurs / typo de marque), pensées pour comparer vite
// format / casse / taille / vitesse d'apparition / angle / couleurs.
// La fonte de scène est Space Grotesk (le stand-in d'Overused Grotesk déjà
// embarqué dans l'app).

interface Ingredient {
  k: string;
  v: string;
}

interface Mode {
  id: string;
  title: string;
  tagline: string;
  built: string;
  text: string; // "\n" = saut de ligne
  upper: boolean;
  pill: boolean;
  fontPx: number;
  tilt: number; // degrés
  anim: "karaoke" | "pop" | "fade" | "cut";
  yPct: number; // centre vertical de la légende, % depuis le haut
  ingredients: Ingredient[];
}

// Ordre = du plus récent (haut du menu) au plus ancien.
const MODES: Mode[] = [
  {
    id: "ref-karaoke",
    title: "Pastille karaoké",
    tagline:
      "Le look de référence LLYLL : pastille Lemonade, texte noir en majuscules, révélation mot par mot (charbon → noir), coupe nette.",
    built: "3 juin 2026 · lot 1",
    text: "je m'appelle Jérémy\net je suis le parent",
    upper: true,
    pill: true,
    fontPx: 19,
    tilt: 0,
    anim: "karaoke",
    yPct: 72,
    ingredients: [
      { k: "Format", v: "Pastille arrondie" },
      { k: "Apparition", v: "Karaoké mot-par-mot" },
      { k: "Casse", v: "MAJUSCULES" },
      { k: "Couleurs", v: "Lemonade #FFFC7D · noir · attente charbon" },
      { k: "Taille", v: "64 px (canvas)" },
      { k: "Angle", v: "0°" },
    ],
  },
  {
    id: "pill-sentence",
    title: "Pastille — sentence case",
    tagline:
      "Même pastille karaoké, casse normale : plus douce que les majuscules, garde le rythme mot par mot.",
    built: "3 juin 2026 · lot 1",
    text: "je m'appelle Jérémy\net je suis le parent",
    upper: false,
    pill: true,
    fontPx: 19,
    tilt: 0,
    anim: "karaoke",
    yPct: 72,
    ingredients: [
      { k: "Format", v: "Pastille arrondie" },
      { k: "Apparition", v: "Karaoké mot-par-mot" },
      { k: "Casse", v: "Sentence case" },
      { k: "Couleurs", v: "Lemonade · noir" },
    ],
  },
  {
    id: "pill-xl-tilt",
    title: "Pastille XL inclinée",
    tagline:
      "Grande taille + légère inclinaison (−3°) + apparition « pop ». Pour les punchlines et l'emphase.",
    built: "3 juin 2026 · lot 1",
    text: "MAGNIFIQUE",
    upper: true,
    pill: true,
    fontPx: 30,
    tilt: -3,
    anim: "pop",
    yPct: 55,
    ingredients: [
      { k: "Format", v: "Pastille arrondie" },
      { k: "Apparition", v: "Pop (scale + overshoot)" },
      { k: "Taille", v: "XL" },
      { k: "Angle", v: "−3°" },
      { k: "Couleurs", v: "Lemonade · noir" },
    ],
  },
  {
    id: "plain-ivory",
    title: "Ivoire sobre",
    tagline:
      "Sans pastille : texte ivoire contouré de noir, coupe nette. La variante discrète et brand-safe sur image chargée.",
    built: "3 juin 2026 · lot 1",
    text: "je m'appelle Jérémy\net je suis le parent",
    upper: false,
    pill: false,
    fontPx: 19,
    tilt: 0,
    anim: "cut",
    yPct: 78,
    ingredients: [
      { k: "Format", v: "Texte libre (sans pastille)" },
      { k: "Apparition", v: "Coupe nette" },
      { k: "Couleurs", v: "Ivoire #D7D2C9 · contour noir" },
    ],
  },
  {
    id: "fade-soft",
    title: "Pastille — fondu doux",
    tagline:
      "Pastille avec une apparition en fondu + montée (≈ 350 ms). Plus posé que la coupe nette, sans karaoké.",
    built: "3 juin 2026 · lot 1",
    text: "bienvenue\nchez LLYLL",
    upper: false,
    pill: true,
    fontPx: 21,
    tilt: 0,
    anim: "fade",
    yPct: 70,
    ingredients: [
      { k: "Format", v: "Pastille arrondie" },
      { k: "Apparition", v: "Fondu + montée (~350 ms)" },
      { k: "Couleurs", v: "Lemonade · noir" },
    ],
  },
];

const UPCOMING = [
  "Autres fontes (Overused Grotesk ExtraBold)",
  "Couleurs custom (hors palette LLYLL)",
  "Slide latéral",
  "Pastille d'emphase manuelle synchronisée",
  "Rendu vidéo réel intégré (ffmpeg)",
];

const STYLES = `
@keyframes mk-karaoke {
  0%, 8% { color: #41413F; }
  16%, 92% { color: #000; }
  100% { color: #41413F; }
}
@keyframes mk-pop {
  0% { opacity: 0; transform: scale(0.8); }
  10% { opacity: 1; transform: scale(1.06); }
  16% { transform: scale(1); }
  88% { opacity: 1; }
  100% { opacity: 0; transform: scale(0.95); }
}
@keyframes mk-fade {
  0% { opacity: 0; transform: translateY(10px); }
  14% { opacity: 1; transform: translateY(0); }
  90% { opacity: 1; }
  100% { opacity: 0; }
}
@keyframes mk-cut {
  0%, 6% { opacity: 0; }
  7%, 94% { opacity: 1; }
  100% { opacity: 0; }
}
`;

function Caption({ mode }: { mode: Mode }) {
  const lines = mode.text.split("\n");
  let wordIndex = 0;

  const outline =
    "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 2px 3px rgba(0,0,0,0.5)";

  const blockAnim =
    mode.anim === "pop"
      ? "mk-pop 3.6s ease-in-out infinite"
      : mode.anim === "fade"
      ? "mk-fade 3.6s ease-in-out infinite"
      : mode.anim === "cut"
      ? "mk-cut 3.6s steps(1, end) infinite"
      : undefined; // karaoké géré mot par mot

  return (
    <div
      style={{
        position: "absolute",
        left: "50%",
        top: `${mode.yPct}%`,
        transform: `translate(-50%, -50%) rotate(${mode.tilt}deg)`,
        width: "88%",
        textAlign: "center",
        animation: blockAnim,
      }}
    >
      <div
        style={{
          display: "inline-block",
          padding: mode.pill ? `${mode.fontPx * 0.32}px ${mode.fontPx * 0.5}px` : 0,
          background: mode.pill ? "#FFFC7D" : "transparent",
          borderRadius: mode.pill ? `${mode.fontPx * 0.42}px` : 0,
          boxShadow: mode.pill ? "0 6px 14px rgba(0,0,0,0.35)" : "none",
          lineHeight: 1.15,
        }}
      >
        {lines.map((line, li) => (
          <div
            key={li}
            style={{
              fontWeight: 800,
              fontSize: `${mode.fontPx}px`,
              letterSpacing: "-0.01em",
              textTransform: mode.upper ? "uppercase" : "none",
              color: mode.pill ? "#000" : "#D7D2C9",
              textShadow: mode.pill ? "none" : outline,
            }}
          >
            {mode.anim === "karaoke"
              ? line.split(" ").map((w) => {
                  const delay = wordIndex * 0.22;
                  wordIndex += 1;
                  return (
                    <span
                      key={`${li}-${wordIndex}`}
                      style={{
                        color: "#41413F",
                        animation: `mk-karaoke 3.8s ease-in-out ${delay}s infinite`,
                      }}
                    >
                      {w}{" "}
                    </span>
                  );
                })
              : line}
          </div>
        ))}
      </div>
    </div>
  );
}

function Phone({ mode }: { mode: Mode }) {
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "9 / 16",
        borderRadius: 18,
        overflow: "hidden",
        background:
          "radial-gradient(120% 80% at 50% 20%, #4a4845 0%, #2f2e2b 55%, #1e1d1b 100%)",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)",
          backgroundSize: "14px 14px",
        }}
      />
      <Caption mode={mode} />
    </div>
  );
}

export default function Mockup() {
  return (
    <div className="min-h-screen bg-ivory text-charcoal">
      <style>{STYLES}</style>

      <header className="border-b border-stone bg-charcoal text-ivory">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-end justify-between gap-6">
          <div>
            <p className="font-mono text-[10.5px] uppercase tracking-wide text-ivory/60">
              videolink · interne
            </p>
            <h1 className="mt-1 font-sans font-black text-lemonade leading-none tracking-tighter text-3xl">
              Mockup · sous-titres
            </h1>
          </div>
          <p className="hidden md:block text-xs text-ivory/60 font-mono max-w-xs text-right leading-relaxed">
            Modes proposés pour le LLYLL Subtitler. Maquettes CSS animées —
            approximation du rendu ffmpeg réel.
          </p>
        </div>
      </header>

      <nav className="sticky top-0 z-10 border-b border-stone bg-ivory/95 backdrop-blur">
        <div className="max-w-5xl mx-auto px-6 py-3 flex gap-2 overflow-x-auto">
          {MODES.map((m, i) => (
            <a
              key={m.id}
              href={`#${m.id}`}
              className="shrink-0 rounded-full border border-stone bg-paper px-3 py-1.5 text-sm font-medium hover:border-charcoal/50 transition"
            >
              <span className="font-mono text-[10px] text-concrete mr-1">
                {String(i + 1).padStart(2, "0")}
              </span>
              {m.title}
            </a>
          ))}
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-14">
        {MODES.map((m) => (
          <section
            key={m.id}
            id={m.id}
            className="scroll-mt-24 grid gap-7 md:grid-cols-[240px_1fr] items-start"
          >
            <div className="w-[240px] max-w-full">
              <Phone mode={m} />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wide text-concrete">
                {m.built}
              </div>
              <h2 className="mt-1 text-2xl font-semibold tracking-tight">{m.title}</h2>
              <p className="mt-2 text-concrete leading-relaxed max-w-xl">{m.tagline}</p>
              <dl className="mt-5 flex flex-wrap gap-2">
                {m.ingredients.map((ing) => (
                  <div
                    key={ing.k}
                    className="rounded-lg border border-stone bg-paper px-3 py-1.5"
                  >
                    <dt className="font-mono text-[9px] uppercase tracking-wide text-ash">
                      {ing.k}
                    </dt>
                    <dd className="text-sm font-medium">{ing.v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>
        ))}

        <section className="rounded-2xl border border-dashed border-stone bg-paper/60 p-6">
          <h2 className="text-lg font-semibold tracking-tight">À venir (prochains lots)</h2>
          <p className="mt-1 text-sm text-concrete">
            Ingrédients pas encore maquettés ici :
          </p>
          <ul className="mt-3 flex flex-wrap gap-2">
            {UPCOMING.map((u) => (
              <li
                key={u}
                className="rounded-full border border-stone px-3 py-1.5 text-sm text-concrete"
              >
                {u}
              </li>
            ))}
          </ul>
        </section>
      </main>

      <footer className="max-w-5xl mx-auto px-6 pb-12 text-xs text-concrete font-mono">
        Page interne · non listée dans la navigation · {MODES.length} modes ·
        maquettes CSS (Space Grotesk). Le rendu final est produit par le LLYLL
        Subtitler (ffmpeg + libass).
      </footer>
    </div>
  );
}
